// T026: Score Calculator

import type { Battlefield, Card, CardDefinition } from '../../types';
import { getCardScore } from '../models/Card';

/**
 * Calculates scores for victory determination.
 */

/**
 * Calculate total score for a player based on battlefield units.
 */
export function calculatePlayerScore(
  battlefield: Battlefield,
  playerSide: 'player1' | 'player2',
  definitions: Map<string, CardDefinition>
): number {
  const side = playerSide === 'player1' ? battlefield.player1Side : battlefield.player2Side;
  let totalScore = 0;

  // Count score from front line
  for (const slot of side.frontLine) {
    if (slot.unit) {
      totalScore += getUnitScore(slot.unit, definitions);
    }
  }

  // Count score from back line
  for (const slot of side.backLine) {
    if (slot.unit) {
      totalScore += getUnitScore(slot.unit, definitions);
    }
  }

  return totalScore;
}

/**
 * Get total score for a unit including merged cards.
 */
function getUnitScore(
  card: Card,
  definitions: Map<string, CardDefinition>
): number {
  return getCardScore(card, definitions);
}

/**
 * Calculate scores for both players.
 */
export function calculateBothScores(
  battlefield: Battlefield,
  definitions: Map<string, CardDefinition>
): { player1Score: number; player2Score: number } {
  return {
    player1Score: calculatePlayerScore(battlefield, 'player1', definitions),
    player2Score: calculatePlayerScore(battlefield, 'player2', definitions),
  };
}

/**
 * Count total units on battlefield for a player.
 */
export function countUnits(
  battlefield: Battlefield,
  playerSide: 'player1' | 'player2'
): number {
  const side = playerSide === 'player1' ? battlefield.player1Side : battlefield.player2Side;
  let count = 0;

  for (const slot of [...side.frontLine, ...side.backLine]) {
    if (slot.unit) count++;
  }

  return count;
}

// Re-export as object for backwards compatibility
export const ScoreCalculator = {
  calculatePlayerScore,
  calculateBothScores,
  countUnits,
};
